// RITA-1561 V44c: Anzeigename im Hochladen-Fenster, zeichengleich mit dateinameFuerScan in
// apps/backend/src/modules/templates/services/contractCreationPrintNames.ts. Bei Änderungen die Regeln
// beider Seiten und ihre Namensfälle gemeinsam prüfen; files.name erlaubt latin1 und höchstens 255 Zeichen.
const PRINT_FILE_NAME_MAX_LENGTH = 255;
export const SCAN_DATEINAME_ZUSATZ = ' - unterschrieben (Scan)';
const DRUCKTAG_BERLIN = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  timeZone: 'Europe/Berlin',
});

const LATIN1_ERSATZ: Readonly<Record<string, string>> = {
  // ohne kanonische Zerlegung
  '\u0141': 'L', // Ł
  '\u0142': 'l', // ł
  '\u0110': 'D', // Đ
  '\u0111': 'd', // đ
  '\u0152': 'OE', // Œ
  '\u0153': 'oe', // œ
  '\u0131': 'i', // ı (türkisches i ohne Punkt)
  '\u0126': 'H', // Ħ
  '\u0127': 'h', // ħ
  '\u0166': 'T', // Ŧ
  '\u0167': 't', // ŧ
  '\u013F': 'L', // Ŀ
  '\u0140': 'l', // ŀ
  '\u014A': 'N', // Ŋ
  '\u014B': 'n', // ŋ
  '\u017F': 's', // ſ (langes s)
  '\u0132': 'IJ', // Ĳ
  '\u0133': 'ij', // ĳ
  '\u1E9E': 'SS', // ẞ (großes ß; latin1 kennt nur ß)
  // mit kanonischer Zerlegung (Grundzeichen wie NFD)
  '\u011E': 'G', // Ğ
  '\u011F': 'g', // ğ
  '\u015E': 'S', // Ş
  '\u015F': 's', // ş
  '\u0218': 'S', // Ș
  '\u0219': 's', // ș
  '\u0162': 'T', // Ţ
  '\u0163': 't', // ţ
  '\u021A': 'T', // Ț
  '\u021B': 't', // ț
  '\u0106': 'C', // Ć
  '\u0107': 'c', // ć
  '\u010C': 'C', // Č
  '\u010D': 'c', // č
  '\u0160': 'S', // Š
  '\u0161': 's', // š
  '\u017D': 'Z', // Ž
  '\u017E': 'z', // ž
  '\u0150': 'O', // Ő
  '\u0151': 'o', // ő
  '\u0170': 'U', // Ű
  '\u0171': 'u', // ű
  '\u0102': 'A', // Ă
  '\u0103': 'a', // ă
  '\u011A': 'E', // Ě
  '\u011B': 'e', // ě
  '\u0158': 'R', // Ř
  '\u0159': 'r', // ř
  '\u0147': 'N', // Ň
  '\u0148': 'n', // ň
  '\u016E': 'U', // Ů
  '\u016F': 'u', // ů
  // RITA-1552 (E1): Striche über U+00FF werden zum Bindestrich statt zu entfallen.
  '\u2013': '-', // – Gedankenstrich
  '\u2014': '-', // — Geviertstrich
};

const LATIN1_MAX = 0xff;

const istSteuerzeichen = (codePoint: number): boolean => codePoint < 0x20 || (codePoint >= 0x7f && codePoint <= 0x9f);

const istPfadtrenner = (zeichen: string): boolean => zeichen === '/' || zeichen === '\\';

/** Ein Zeichen (ein Codepunkt) in seine latin1-Entsprechung; Steuerzeichen und Pfadtrenner entfallen. */
const latin1Zeichen = (zeichen: string): string => {
  const codePoint = zeichen.codePointAt(0) ?? 0;
  if (codePoint <= LATIN1_MAX) {
    return istSteuerzeichen(codePoint) || istPfadtrenner(zeichen) ? '' : zeichen;
  }
  const ersatz = LATIN1_ERSATZ[zeichen];
  if (ersatz !== undefined) return ersatz;
  // Zerlegen und nur Grundzeichen innerhalb latin1 behalten; Kombinationszeichen und fremde Schriften entfallen.
  return Array.from(zeichen.normalize('NFD'))
    .filter((teil) => (teil.codePointAt(0) ?? 0) <= LATIN1_MAX)
    .join('');
};

/**
 * Text für die latin1-Spalte files.name: Zeichen über U+00FF werden ersetzt oder entfernt (siehe Kopf), alles
 * andere bleibt. Zuerst NFC, damit zerlegt eingegebene Umlaute (u + U+0308, macOS) als ü erhalten bleiben.
 * Steuerzeichen sowie „/“ und „\“ entfallen, Mehrfach-Leerzeichen werden eins, Ränder getrimmt, Länge höchstens
 * PRINT_FILE_NAME_MAX_LENGTH. Mehrfache Anwendung ändert nichts mehr.
 */
function latin1Sicher(text: string): string {
  return Array.from(text.normalize('NFC'))
    .map(latin1Zeichen)
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, PRINT_FILE_NAME_MAX_LENGTH)
    .trim();
}

/** Datum und Zusatz bleiben bei langen Namen vollständig; das Datum gehört zum Vertragsdruck. */
export function scanDateiname(beraterName: string, druckdatumIso: string): string {
  const zeit = new Date(druckdatumIso);
  const datum = Number.isNaN(zeit.getTime()) ? '' : ` ${DRUCKTAG_BERLIN.format(zeit)}`;
  const art = 'Vertrag';
  const schluss = `${datum}${SCAN_DATEINAME_ZUSATZ}.pdf`;
  const platz = PRINT_FILE_NAME_MAX_LENGTH - art.length - 1 - schluss.length;
  const name = latin1Sicher(beraterName).slice(0, platz).trim();
  return name ? `${art} ${name}${schluss}` : `${art}${schluss}`;
}
